import React, { useEffect, useRef, useState } from "react"
import { Link } from "gatsby"
import Cookies from "universal-cookie"
import { CookieMeta } from "../data/seo-meta"
import { stopTracking, trackPage } from "./snowplow"

const cookieName = "slice-cookie-preferences"
const version = 1
const maxAge = 180 * 24 * 60 * 60
const openEvent = "slice:open-cookie-settings"

export const readConsent = () => {
  const value = new Cookies().get(cookieName)
  return value &&
    value.version === version &&
    typeof value.analytics === "boolean" &&
    Date.now() - value.savedAt < maxAge * 1000
    ? value
    : null
}

export const openCookieSettings = () =>
  document.dispatchEvent(new CustomEvent(openEvent))

const CookiePolicy = () => {
  const dialogRef = useRef(null)
  const [showBanner, setShowBanner] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [status, setStatus] = useState("")

  useEffect(() => {
    setShowBanner(!readConsent())
    const open = () => {
      const consent = readConsent()
      setAnalytics(!!consent && consent.analytics)
      dialogRef.current.showModal()
      document.body.classList.add("cookie-dialog-open")
    }
    const unlock = () => document.body.classList.remove("cookie-dialog-open")
    const dialog = dialogRef.current
    document.addEventListener(openEvent, open)
    dialog.addEventListener("close", unlock)
    return () => {
      document.removeEventListener(openEvent, open)
      dialog.removeEventListener("close", unlock)
      unlock()
    }
  }, [])

  useEffect(() => {
    if (!status) return
    const timer = setTimeout(() => setStatus(""), 4500)
    return () => clearTimeout(timer)
  }, [status])

  const save = (allowAnalytics) => {
    const previous = readConsent()
    new Cookies().set(
      cookieName,
      { version, analytics: allowAnalytics, savedAt: Date.now() },
      {
        path: "/",
        maxAge,
        sameSite: "lax",
        secure: window.location.protocol === "https:",
      }
    )
    setShowBanner(false)
    if (dialogRef.current.open) dialogRef.current.close()
    setStatus(
      allowAnalytics
        ? "Preferences saved. Analytics allowed."
        : "Preferences saved. Optional cookies rejected."
    )
    if (allowAnalytics && !(previous && previous.analytics)) trackPage()
    if (!allowAnalytics) stopTracking()
  }

  return (
    <>
      <section
        className="cookie-banner"
        aria-labelledby="cookie-title"
        hidden={!showBanner}
      >
        <div className="cookie-inner">
          <div className="cookie-message">
            <h2 id="cookie-title">A little about cookies</h2>
            <p>
              Essential cookies keep this site working. Optional analytics
              cookies help us understand what’s useful and improve your
              experience. You choose whether to allow them.
            </p>
            <div className="cookie-links">
              <button type="button" onClick={openCookieSettings}>
                Manage preferences
              </button>
              <Link to={CookieMeta.path}>Cookie Policy</Link>
            </div>
          </div>
          <div className="cookie-actions">
            <button
              type="button"
              className="cookie-choice"
              onClick={() => save(false)}
            >
              Reject optional
            </button>
            <button
              type="button"
              className="cookie-choice"
              onClick={() => save(true)}
            >
              Accept optional
            </button>
          </div>
        </div>
      </section>
      <dialog
        ref={dialogRef}
        className="cookie-dialog"
        aria-labelledby="cookie-dialog-title"
        aria-describedby="cookie-dialog-description"
      >
        <form method="dialog">
          <div className="cookie-dialog-top">
            <p className="cookie-eyebrow">YOUR PRIVACY</p>
            <button
              className="cookie-close"
              aria-label="Close cookie preferences"
              value="cancel"
            >
              ×
            </button>
          </div>
          <h2 id="cookie-dialog-title">Cookie preferences</h2>
          <p id="cookie-dialog-description">
            Choose whether to allow analytics cookies. You can change your
            choice anytime using Cookie settings in the footer.
          </p>
          <div className="cookie-category">
            <div>
              <h3>Essential cookies</h3>
              <p>Help the website work and remember your cookie preferences.</p>
            </div>
            <span className="cookie-required">Always on</span>
          </div>
          <div className="cookie-category">
            <div>
              <h3>
                <label htmlFor="cookie-analytics">Analytics cookies</label>
              </h3>
              <p>
                Help us understand visits and interactions so we can improve the
                website.
              </p>
            </div>
            <input
              type="checkbox"
              role="switch"
              id="cookie-analytics"
              aria-label="Allow analytics cookies"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
            />
          </div>
          <Link className="cookie-policy-link" to={CookieMeta.path}>
            Read our Cookie Policy
          </Link>
          <div className="cookie-dialog-actions">
            <button
              type="button"
              className="cookie-choice"
              onClick={() => save(false)}
            >
              Reject optional cookies
            </button>
            <button
              type="button"
              className="cookie-save"
              onClick={() => save(analytics)}
            >
              Save preferences
            </button>
          </div>
        </form>
      </dialog>
      <div className="cookie-status" role="status" aria-live="polite">
        {status}
      </div>
    </>
  )
}

export default CookiePolicy
